/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2020 (original work) Open Assessment Technologies SA ;
 */
import { getActualKey } from '@oat-sa-private/ui-core';

/**
 * @deprecated use `import {arrowKeysFocusLoop} from '@oat-sa-private/ui-core'`
 *
 * Svelte Action to focus previous and next elements inside a container on arrow keys
 *
 * @example
 * <g
 *   use:arrowKeysFocusLoop={{choices, choiceKeysInTabOrder, lastFocusedChoiceKey, focusChoiceWithKey, isRTL}}
 *   on:setLastFocusedChoiceKey
 *   on:setHasFocus>
 *   {#each choices as choice}
 *     <g class="choice" {...choice} />
 *   {/each}
 * </g>
 *
 * @param {HTMLElement} node - container node inside which the focusables reside
 * @param {Object} options
 * @param {String[]} options.choiceKeysInTabOrder
 * @param {String} options.lastFocusedChoiceKey
 * @param {Function} options.focusChoiceWithKey - callback: (newFocusedChoiceKey: String) => void
 * @param {Boolean} [options.isRTL=false]
 * @returns {Object} - node's lifecycle hooks
 */
export default function arrowKeysFocusLoop(
    node,
    { choiceKeysInTabOrder, lastFocusedChoiceKey, focusChoiceWithKey, isRTL = false }
) {
    /**
     * Focus a sibling choice in the tabbing order
     * Supports looping from last to first
     * @param {Number} [offset=1] use 1 for next, -1 for previous
     * @fires setLastFocusedChoiceKey
     * @fires setHasFocus
     */
    function focusSibling(offset = 1) {
        if (lastFocusedChoiceKey) {
            const index = choiceKeysInTabOrder.indexOf(lastFocusedChoiceKey);
            const newIndex = (choiceKeysInTabOrder.length + index + offset) % choiceKeysInTabOrder.length;
            const newFocusedChoiceKey = choiceKeysInTabOrder[newIndex];
            if (focusChoiceWithKey(newFocusedChoiceKey)) {
                lastFocusedChoiceKey = newFocusedChoiceKey;
                node.dispatchEvent(
                    new CustomEvent('setLastFocusedChoiceKey', {
                        detail: newFocusedChoiceKey,
                        bubbles: true
                    })
                );
                node.dispatchEvent(
                    new CustomEvent('setHasFocus', {
                        detail: true,
                        bubbles: true
                    })
                );
            }
        }
    }

    /**
     * Handle key navigation (only arrow keys move between available choices)
     * @param {KeyboardEvent} event
     */
    function handleKeyDown(event) {
        const pressedKey = getActualKey(event);
        switch (pressedKey) {
            case 'down':
            case !isRTL && 'right':
            case isRTL && 'left':
                event.preventDefault();
                focusSibling(1);
                break;

            case 'up':
            case !isRTL && 'left':
            case isRTL && 'right':
                event.preventDefault();
                focusSibling(-1);
                break;
        }
    }

    // Initialise the listener
    node.addEventListener('keydown', handleKeyDown);

    return {
        /**
         * Hook on component lifecyle: remove listener
         */
        destroy() {
            node.removeEventListener('keydown', handleKeyDown);
        },

        /**
         * Hook to update action params
         * @param {Object} newOptions
         */
        update(newOptions) {
            ({ choiceKeysInTabOrder, lastFocusedChoiceKey, focusChoiceWithKey } = newOptions);
        }
    };
}
